import {useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'

import useUser from '../../hooks/useUser'
import {ActionButton, RedirectButton, NavButton} from '../../components/forms/inputs'

import {getModelConnections, removeSubscription} from '../../services/evaluation'
import {manageSubscription} from '../../services/users'
import type {
  Evaluation as EvaluationType,
  Model as ModelType,
  ModelConnection,
  ItemType,
} from '../../services/types'
import Path from '../../routeLinks'

type CurrentConnectionProps = {
  connection: ModelConnection
  onDelete: (connection: ModelConnection) => Promise<any>
  itemType: ItemType
}
export const CurrentConnection = ({connection, onDelete, itemType}: CurrentConnectionProps) => {
  const {cadence} = connection
  const item = connection[itemType as 'model' | 'evaluation']
  const to =
    item && (itemType === 'evaluation' ? Path.evaluations.show(item.id) : Path.models.show(item.id))
  return (
    <Card>
      <CardContent>
        <Stack direction={{xs: 'column', sm: 'row'}} spacing={6}>
          {item && <NavButton label={item.name} to={to || ''} />}
          <Box sx={{flex: 2}} />
          <Typography>{cadence}</Typography>
          <ActionButton
            label="Cancel"
            endIcon={<CloseIcon />}
            action={() => onDelete(connection)}
            confirmationText="Are you sure you want to cancel this scheduled run?"
          />
        </Stack>
      </CardContent>
    </Card>
  )
}

type ShowCurrentProps = {
  item: ModelType | EvaluationType
  itemsType: ItemType
}
export const ShowCurrentConnections = ({item, itemsType}: ShowCurrentProps) => {
  const [connections, setConnections] = useState<ModelConnection[]>([])
  const {user} = useUser()
  const navigate = useNavigate()

  useEffect(() => {
    const getConnections = async () => {
      try {
        const {items: connections} = await getModelConnections(
          item,
          itemsType === 'model' ? 'evaluation' : 'model'
        )
        setConnections(connections)
      } catch (e) {
        if (e === `You are not allowed to modify this ${itemsType}`) {
          navigate(Path.evaluations.show(item.id))
        }
      }
    }
    if (item) {
      getConnections()
    }
  }, [item, navigate, itemsType])

  const manageSubscriptions = async () =>
    user && (await manageSubscription(user, 'evaluations')).manage_subscription_url
  const deleteConnection = async (connection: ModelConnection) => {
    await removeSubscription(connection)
    setConnections((cons) => cons.filter((c) => c.id !== connection.id))
  }
  const sortConnection = (a: ModelConnection, b: ModelConnection) => {
    if (a.cadence === b.cadence) {
      return (a.name || a.model?.name || '')
        .toLowerCase()
        .localeCompare((b.name || b.model?.name || '').toLowerCase())
    }
    const cadences = ['once', 'daily', 'weekly', 'every 2 weeks', 'monthly', 'quarterly']
    return cadences.indexOf(a.cadence) - cadences.indexOf(b.cadence)
  }

  if (connections.length === 0) return null

  return (
    <Stack sx={{pt: 4}}>
      <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between">
        <Typography variant="h4" sx={{color: 'primary.main'}}>
          Your scheduled runs:
        </Typography>
        <RedirectButton label="Manage subscriptions" urlGetter={manageSubscriptions} />
      </Stack>
      <Box component="div">
        {connections.sort(sortConnection).map((con) => (
          <CurrentConnection
            key={con.id}
            connection={con}
            onDelete={deleteConnection}
            itemType={itemsType}
          />
        ))}
      </Box>
    </Stack>
  )
}

export default ShowCurrentConnections
